import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './SnapTour.module.css';
import { ChevronIcon } from './MarketingIcons';
import useMediaQuery from '../../hooks/useMediaQuery';

/**
 * Non-autoplay tour. On a phone the slides snap horizontally with a peek of
 * the next card; at desktop width a `grid` layout shows every slide at once;
 * `single` keeps one slide in view at every width. Reduced-motion stacks a
 * grid tour and still allows a single tour to be stepped without animation.
 */
const SnapTour = ({ slides, renderSlide, ariaLabel, layout = 'grid' }) => {
  const scrollerRef = useRef(null);
  const slideRefs = useRef([]);
  const [index, setIndex] = useState(0);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const scrollTo = useCallback(
    (next) => {
      const clamped = Math.max(0, Math.min(slides.length - 1, next));
      setIndex(clamped);
      const target = slideRefs.current[clamped];
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          inline: 'start',
          block: 'nearest',
        });
      }
    },
    [prefersReducedMotion, slides.length]
  );

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const nextIndex = slideRefs.current.indexOf(entry.target);
          if (nextIndex >= 0) setIndex(nextIndex);
        });
      },
      { root, threshold: 0.55 }
    );

    slideRefs.current.forEach((node) => {
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [slides.length]);

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollTo(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollTo(index - 1);
    }
  };

  return (
    <div
      className={`${styles.tour} ${layout === 'single' ? styles.single : styles.grid}`}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      <div
        className={styles.scroller}
        ref={scrollerRef}
        tabIndex={0}
        aria-label={`${ariaLabel} slides`}
      >
        <ul className={styles.track}>
          {slides.map((slide, slideIndex) => (
            <li
              key={slide.id || slide.title || slideIndex}
              className={styles.slide}
              ref={(node) => {
                slideRefs.current[slideIndex] = node;
              }}
              aria-label={`${slideIndex + 1} of ${slides.length}`}
            >
              {renderSlide(slide, slideIndex)}
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.arrow}
          onClick={() => scrollTo(index - 1)}
          disabled={index === 0}
          aria-label="Previous slide"
        >
          <ChevronIcon className={styles.arrowPrev} />
        </button>
        <div className={styles.dots}>
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.id || slide.title || slideIndex}
              type="button"
              className={`${styles.dot} ${slideIndex === index ? styles.dotActive : ''}`}
              aria-label={`Show slide ${slideIndex + 1} of ${slides.length}`}
              aria-current={slideIndex === index ? 'true' : undefined}
              onClick={() => scrollTo(slideIndex)}
            />
          ))}
        </div>
        <button
          type="button"
          className={styles.arrow}
          onClick={() => scrollTo(index + 1)}
          disabled={index === slides.length - 1}
          aria-label="Next slide"
        >
          <ChevronIcon className={styles.arrowNext} />
        </button>
      </div>
    </div>
  );
};

export default SnapTour;
